import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import {
  Chat,
  Channel,
  ChannelList,
  Window,
  ChannelHeader,
  ChannelAvatar,
  MessageList,
  MessageComposer,
  Thread,
  WithComponents,
  AttachmentSelector,
  defaultAttachmentSelectorActionSet,
  useChatContext,
  useChannelStateContext,
  useChannelMembershipState,
} from "stream-chat-react";
import "stream-chat-react/dist/css/index.css";
import "./Chat.css";
import SidebarMenu from "../Widgets/SideBar Menu/SidebarMenu";
import { useStaffChat } from "./StaffChatProvider";
import NewDirectChatModal from "./NewDirectChatModal";
import NewGroupModal from "./NewGroupModal";
import GroupMembersModal from "./GroupMembersModal";

// Newest activity first. A chat without messages is sorted by the time it was created.
const CHANNEL_SORT = { last_updated: -1 };
const CHANNEL_OPTIONS = { presence: true, state: true, watch: true, limit: 30 };

// Read only view: no reply / react / edit / delete on the messages
const NO_MESSAGE_ACTIONS = [];

const StaffChatUiContext = createContext({
  openNewChat: () => {},
  openNewGroup: () => {},
  canViewAllChats: false,
  viewAllChats: false,
  switchView: () => {},
});

// Super Admin only: true when the open chat belongs to other staff (he is not a member of it).
// Such a chat is shown read only.
const useIsReadOnlyChat = (channel) => {
  const { canViewAllChats, currentUserId } = useStaffChat();
  const membership = useChannelMembershipState(channel);

  if (!canViewAllChats || !channel) {
    return false;
  }

  const isMember =
    typeof membership?.user !== "undefined" ||
    typeof channel.state?.members?.[currentUserId] !== "undefined";

  return !isMember;
};

// Header of the chat list: title + New chat / New group buttons.
// Super Admin also gets the My chats / All chats switch.
const StaffChannelListHeader = () => {
  const { openNewChat, openNewGroup, canViewAllChats, viewAllChats, switchView } =
    useContext(StaffChatUiContext);
  const { setActiveChannel } = useChatContext();

  const changeView = (viewAll) => {
    // The open chat belongs to the other view
    setActiveChannel(undefined);
    switchView(viewAll);
  };

  return (
    <div className="staff-chat__list-header-wrap">
      <div className="str-chat__channel-list__header staff-chat__list-header">
        <div className="str-chat__channel-list__header__title">Chats</div>

        <div className="staff-chat__list-actions">
          <Button
            size="sm"
            variant="light"
            className="staff-chat__new-btn"
            onClick={openNewChat}
          >
            <i className="bi bi-pencil-square me-1"></i>
            New chat
          </Button>
          <Button
            size="sm"
            variant="light"
            className="staff-chat__new-btn"
            onClick={openNewGroup}
          >
            <i className="bi bi-people-fill me-1"></i>
            New group
          </Button>
        </div>
      </div>

      {canViewAllChats && (
        <div className="staff-chat__view-switch">
          <button
            type="button"
            className={`staff-chat__view-btn ${
              !viewAllChats ? "staff-chat__view-btn--active" : ""
            }`}
            onClick={() => changeView(false)}
          >
            My chats
          </button>
          <button
            type="button"
            className={`staff-chat__view-btn ${
              viewAllChats ? "staff-chat__view-btn--active" : ""
            }`}
            onClick={() => changeView(true)}
          >
            All chats
          </button>
        </div>
      )}
    </div>
  );
};

// Right side of the open chat's header: Read only label, Members button (groups only) + avatar
const StaffChannelHeaderEnd = (props) => {
  const { channel } = useChannelStateContext();
  const { fullFeatures } = useStaffChat();
  const readOnly = useIsReadOnlyChat(channel);
  const [showMembersModal, setShowMembersModal] = useState(false);

  const isGroup = channel?.data?.is_group === true;

  return (
    <div className="staff-chat__header-end">
      {readOnly && <span className="staff-chat__readonly-badge">Read only</span>}

      {isGroup && (
        <Button
          size="sm"
          variant="light"
          className="staff-chat__new-btn"
          onClick={() => setShowMembersModal(true)}
        >
          <i className="bi bi-people-fill me-1"></i>
          Members
        </Button>
      )}

      <ChannelAvatar {...props} />

      {isGroup && (
        <GroupMembersModal
          show={showMembersModal}
          onHide={() => setShowMembersModal(false)}
          channel={channel}
          readOnly={readOnly}
          canManage={fullFeatures}
        />
      )}
    </div>
  );
};

// The open chat: header, messages, message box and threads.
// A chat of other staff opened by the Super Admin has no message box and no message actions.
const StaffChannelBody = () => {
  const { channel } = useChannelStateContext();
  const readOnly = useIsReadOnlyChat(channel);

  return (
    <>
      <Window>
        <ChannelHeader Avatar={StaffChannelHeaderEnd} />
        <MessageList messageActions={readOnly ? NO_MESSAGE_ACTIONS : undefined} />
        {readOnly ? (
          <div className="staff-chat__readonly-notice">
            <i className="bi bi-eye-fill me-2"></i>
            You are viewing this chat as Super Admin. It is read only.
          </div>
        ) : (
          <MessageComposer focus />
        )}
      </Window>
      <Thread messageActions={readOnly ? NO_MESSAGE_ACTIONS : undefined} />
    </>
  );
};

// Close the open chat when the user is removed from it (or it is deleted)
const ActiveChannelGuard = () => {
  const { client, channel, setActiveChannel } = useChatContext();

  useEffect(() => {
    if (!channel) {
      return;
    }

    const closeIfActive = (event) => {
      const cid = event.channel?.cid || event.cid;
      if (cid === channel.cid) {
        setActiveChannel(undefined);
      }
    };

    const removedListener = client.on(
      "notification.removed_from_channel",
      closeIfActive
    );
    const deletedListener = client.on("channel.deleted", closeIfActive);

    return () => {
      removedListener.unsubscribe();
      deletedListener.unsubscribe();
    };
  }, [client, channel, setActiveChannel]);

  return null;
};

// Chat opened from a message notification (/admin/chat?channel=...):
// once that chat is open, the list is free again to follow the user's own clicks
const OpenedFromLinkWatcher = ({ channelId, onOpened }) => {
  const { channel } = useChatContext();

  useEffect(() => {
    if (channelId && channel?.id === channelId) {
      onOpened();
    }
  }, [channelId, channel, onOpened]);

  return null;
};

// Message box "+" button: only File (no Commands / Poll / Location).
// With File alone, the button opens the file picker directly instead of a one item menu.
const FILE_ONLY_ACTION_SET = defaultAttachmentSelectorActionSet.filter(
  (action) => action.type === "uploadFile"
);

const StaffAttachmentSelector = (props) => (
  <AttachmentSelector
    {...props}
    attachmentSelectorActionSet={FILE_ONLY_ACTION_SET}
  />
);

const COMPONENT_OVERRIDES = {
  ChannelListHeader: StaffChannelListHeader,
  AttachmentSelector: StaffAttachmentSelector,
};

// Group chat only mode: finds the user's group chat and opens it (there is no list of chats).
// The most recently active group is opened when the user is in more than one.
const DefaultGroupOpener = ({ onStatusChange }) => {
  const { client, channel, setActiveChannel } = useChatContext();
  const { currentUserId } = useStaffChat();
  const [retry, setRetry] = useState(0);

  const hasOpenChat = !!channel;

  useEffect(() => {
    if (hasOpenChat) {
      return;
    }

    let cancelled = false;

    const openGroup = async () => {
      onStatusChange("loading");

      try {
        let group = null;

        for (let page = 0; page < 5 && !group; page++) {
          const channels = await client.queryChannels(
            { type: "messaging", members: { $in: [currentUserId] } },
            CHANNEL_SORT,
            {
              limit: 30,
              offset: page * 30,
              state: true,
              watch: false,
              presence: false,
            }
          );
          if (cancelled) {
            return;
          }

          group = channels.find((item) => item.data?.is_group === true) || null;

          if (channels.length < 30) {
            break;
          }
        }

        if (!group) {
          onStatusChange("none");
          return;
        }

        await group.watch({ presence: true });
        if (cancelled) {
          return;
        }

        setActiveChannel(group);
        onStatusChange("ready");
      } catch (error) {
        console.error("Error opening the group chat:", error);
        if (!cancelled) {
          onStatusChange("error");
        }
      }
    };

    openGroup();

    // Added to a group while this page is open
    const addedListener = client.on("notification.added_to_channel", () => {
      setRetry((count) => count + 1);
    });

    return () => {
      cancelled = true;
      addedListener.unsubscribe();
    };
  }, [
    client,
    currentUserId,
    hasOpenChat,
    retry,
    setActiveChannel,
    onStatusChange,
  ]);

  return null;
};

const GROUP_ONLY_NOTICES = {
  loading: "Loading the group chat...",
  none: "No group chat is available for your account yet.",
  error: "The group chat could not be loaded. Please refresh the page.",
};

// REACT_APP_AQATO_ADMIN_PORTAL_CHAT_FULL_FEATURES = false:
// only the open group chat is shown (no list, no New chat / New group, no My chats / All chats)
const StaffChatGroupOnly = () => {
  const { client } = useStaffChat();
  const [groupStatus, setGroupStatus] = useState("loading");

  return (
    <div className="staff-chat staff-chat--group-only">
      <Chat client={client} theme="str-chat__theme-light">
        <WithComponents overrides={COMPONENT_OVERRIDES}>
          <ActiveChannelGuard />
          <DefaultGroupOpener onStatusChange={setGroupStatus} />

          <Channel
            EmptyPlaceholder={
              <div className="staff-chat-notice staff-chat-notice--inside">
                {groupStatus === "loading" && (
                  <Spinner animation="border" size="sm" className="me-2" />
                )}
                {GROUP_ONLY_NOTICES[groupStatus] || GROUP_ONLY_NOTICES.loading}
              </div>
            }
          >
            <StaffChannelBody />
          </Channel>
        </WithComponents>
      </Chat>
    </div>
  );
};

const StaffChatFull = () => {
  const { client, currentUserId, canViewAllChats } = useStaffChat();

  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);

  // Super Admin: false = his own chats, true = the chats of all the staff
  const [viewAllChats, setViewAllChats] = useState(false);

  // version: changed to load the chat list again (group created, view switched).
  // autoSelect: open the first chat of the list after it is loaded.
  const [listState, setListState] = useState({ version: 0, autoSelect: true });

  // Chat to open first, when the page is opened from a message notification
  const [searchParams] = useSearchParams();
  const [linkedChannelId, setLinkedChannelId] = useState(
    () => searchParams.get("channel") || undefined
  );
  const clearLinkedChannelId = useCallback(
    () => setLinkedChannelId(undefined),
    []
  );

  const showAll = canViewAllChats && viewAllChats;

  const ui = useMemo(
    () => ({
      openNewChat: () => setShowNewChatModal(true),
      openNewGroup: () => setShowNewGroupModal(true),
      canViewAllChats: canViewAllChats,
      viewAllChats: showAll,
      // Clicking the view which is already open loads its list again
      switchView: (viewAll) => {
        setViewAllChats(viewAll);
        setListState((previous) => ({
          version: previous.version + 1,
          autoSelect: true,
        }));
      },
    }),
    [canViewAllChats, showAll]
  );

  // My chats: only the chats where the logged in user is a member.
  // All chats (Super Admin): every chat of the organisation. Stream refuses this list to everyone else.
  const filters = useMemo(
    () =>
      showAll
        ? { type: "messaging" }
        : { type: "messaging", members: { $in: [currentUserId] } },
    [showAll, currentUserId]
  );

  // Leaving "All chats": stop listening to the chats of the other staff
  useEffect(() => {
    if (!showAll) {
      return;
    }

    return () => {
      Object.values(client.activeChannels || {}).forEach((channel) => {
        const isMember =
          typeof channel.state?.membership?.user !== "undefined" ||
          typeof channel.state?.members?.[currentUserId] !== "undefined";

        if (!isMember && channel.initialized) {
          channel.stopWatching().catch(() => {});
        }
      });
    };
  }, [showAll, client, currentUserId]);

  return (
    <div className="staff-chat">
      <Chat client={client} theme="str-chat__theme-light">
        <StaffChatUiContext.Provider value={ui}>
          <WithComponents overrides={COMPONENT_OVERRIDES}>
            <ActiveChannelGuard />
            <OpenedFromLinkWatcher
              channelId={linkedChannelId}
              onOpened={clearLinkedChannelId}
            />

            <ChannelList
              key={listState.version}
              filters={filters}
              sort={CHANNEL_SORT}
              options={CHANNEL_OPTIONS}
              customActiveChannel={linkedChannelId}
              setActiveChannelOnMount={listState.autoSelect}
            />

            <Channel>
              <StaffChannelBody />
            </Channel>

            <NewDirectChatModal
              show={showNewChatModal}
              onHide={() => setShowNewChatModal(false)}
            />
            <NewGroupModal
              show={showNewGroupModal}
              onHide={() => setShowNewGroupModal(false)}
              onCreated={() =>
                setListState((previous) => ({
                  version: previous.version + 1,
                  autoSelect: false,
                }))
              }
            />
          </WithComponents>
        </StaffChatUiContext.Provider>
      </Chat>
    </div>
  );
};

const ChatPage = () => {
  const { ready, status, fullFeatures } = useStaffChat();

  return (
    <>
      <div className="main_desktop">
        <hr className="mb-2 thick-hr" />
        <div className="main-wrapper">
          <div className="sub-wrapper-sidebar">
            <SidebarMenu />
          </div>
          <div className="sub-wrapper-main">
            <div className="main-heading-title">
              <h3 className="heading-admin-title text-color-green">Chat</h3>
            </div>

            {ready && (fullFeatures ? <StaffChatFull /> : <StaffChatGroupOnly />)}

            {!ready && status === "disabled" && (
              <div className="staff-chat-notice">
                Chat is not available for this account.
              </div>
            )}

            {!ready && status === "unavailable" && (
              <div className="staff-chat-notice">
                Chat is not available right now. Please refresh the page or try
                again in a few minutes.
              </div>
            )}

            {!ready && status !== "disabled" && status !== "unavailable" && (
              <div className="staff-chat-notice">
                <Spinner animation="border" size="sm" className="me-2" />
                Connecting to chat...
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatPage;
